// Eager durability net for the room-capture "create then catch-up" path.
//
// A fresh (never-captured) asset's FIRST +/- tap dispatches a `createItem`
// carrying quantity 1; that INSERT reaches durable ps_crud immediately. Taps
// 2..N do NOT dispatch — they only accumulate the latest quantity in the
// RoomCaptureScreen's `pendingCreatesRef` and rely on the screen reconciler
// (a `client.subscribe`) firing a catch-up `updateItem` to the final quantity
// WHEN the create's echo arrives.
//
// On a slow device (phone WASM SQLite) that echo lands hundreds of ms later.
// If the surveyor leaves the room first, React unmounts the screen and tears
// down that subscription, so the catch-up NEVER runs and only the first tap's 1
// persists — the "set the quantity to 3, came back and it was 1" field report,
// reproduced on the live @powersync/web + Encore + Postgres harness (a
// leave-before-echo run leaves the server holding 1). The #817 flush-on-unmount
// cannot help here: the catch-up was never queued, so the coalesce is empty.
//
// So queue the catch-up EAGERLY, at tap time, behind the create dispatch's
// resolved row id (the same id the create path inserted, including tombstone
// fallback ids), as an ordinary coalesced `updateItem`. It is durable
// independent of the echo/unmount race:
//   - the #817 unmount flush (and the 500 ms coalesce debounce) drain it, and
//   - the create's local INSERT has already resolved before this UPDATE is
//     dispatched, so the UPDATE lands on the created row rather than no-opping
//     against a missing one.
// The reconciler catch-up still runs when the echo beats the unmount; both
// target the same id and set the same ABSOLUTE quantity, so they converge under
// last-write-wins with no double-count. Older queued catch-ups abort when a
// newer tap replaces the pending entry, so async promise resolution cannot
// replay an earlier quantity over the latest one.

import type { CaptureClient, SessionSummary } from '@citycare/shared'

import {
  isCaptureItemRow,
  type PendingCreate,
} from './room-capture-reconcile'

type FreshCaptureClient = Pick<CaptureClient, 'dispatch'> & {
  flush?: () => Promise<void>
}

/**
 * Queue a durable catch-up `updateItem` after the in-flight create resolves
 * with its inserted capture_items id, so taps taken AFTER the first survive a
 * room-exit that unmounts the reconciler before the create echo fires. Fully
 * fire-and-forget: rejections are swallowed because the reconciler catch-up
 * remains the primary path when the echo arrives before the surveyor leaves.
 *
 * `pending` carries the LATEST optimistic quantity / flags — the same entry
 * recorded in `pendingCreatesRef`.
 */
function queueEagerCreateCatchUp(
  client: FreshCaptureClient,
  pendingCreates: Map<string, PendingCreate>,
  pendingKey: string,
  pending: PendingCreate,
): void {
  void pending.createResult
    ?.then((result) => {
      const latest = pendingCreates.get(pendingKey)
      if (latest !== pending) return undefined
      if (result.kind !== 'createItem' || !isCaptureItemRow(result.item)) {
        return undefined
      }
      const updateResult = client.dispatch({
        kind: 'updateItem',
        itemId: result.item.id,
        quantity: latest.quantity,
        isZeroConfirmed: latest.isZeroConfirmed,
        notes: latest.notes,
        syncVersion: result.item.syncVersion,
      })
      void client.flush?.().catch(() => {
        // Best-effort immediate durability net — see the file header.
      })
      pendingCreates.delete(pendingKey)
      return updateResult
    })
    .catch(() => {
      // Best-effort durability net — see the file header.
    })
}

export interface FreshCaptureInput {
  session: Pick<SessionSummary, 'id' | 'siteId'>
  spaceId: string
  assetTypeId: string
  /** Latest optimistic values (the entry stored in `pendingCreatesRef`). */
  pending: PendingCreate
  /** Room-scoped pending key; asset types can repeat across rooms. */
  pendingKey: string
  /** True when a createItem for this slot is already in flight (a prior tap). */
  alreadyPending: boolean
}

/**
 * Dispatch the write for a FRESH (never-captured) asset tap. On the first tap
 * (`alreadyPending === false`) it fires the `createItem` (an INSERT that reaches
 * durable ps_crud immediately); its rejection drops the slot from
 * `pendingCreates` so a later tap re-creates it. On a subsequent tap it queues
 * the durable eager catch-up behind that create instead (see
 * `queueEagerCreateCatchUp`) — this is the fix for the "set 3, came back to 1"
 * leave-before-echo race.
 */
export function dispatchFreshCapture(
  client: FreshCaptureClient,
  pendingCreates: Map<string, PendingCreate>,
  input: FreshCaptureInput,
): void {
  const { session, spaceId, assetTypeId, pending, pendingKey, alreadyPending } =
    input
  if (alreadyPending) {
    queueEagerCreateCatchUp(
      client,
      pendingCreates,
      pendingKey,
      pending,
    )
    return
  }
  const createResult = client
    .dispatch({
      kind: 'createItem',
      sessionId: session.id,
      spaceId,
      assetTypeId,
      quantity: pending.quantity,
      isZeroConfirmed: pending.isZeroConfirmed,
      notes: pending.notes,
    })
    .catch((err: unknown) => {
      pendingCreates.delete(pendingKey)
      throw err
    })
  pending.createResult = createResult
  void createResult.catch(() => {
    // Rejection is already reflected by dropping pendingCreates above.
  })
}
